import { Badge } from "@/components/ui/badge";
import { MessageLogMetadata as MetadataType } from "@common/models/message-log";
import { Wifi, WifiOff, QrCode, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

interface MessageLogMetadataProps {
    metadata?: MetadataType;
    className?: string;
    retryCount?: number; // Message retryCount to calculate attempt number
}

const attemptTypeLabels: Record<string, string> = {
    'initial': 'Initial Attempt',
    'client-retry': 'Client Retry',
    'scheduler-retry': 'Scheduler Retry',
};

const attemptTypeColors: Record<string, string> = {
    'initial': 'bg-blue-100 text-blue-800 border-blue-300',
    'client-retry': 'bg-orange-100 text-orange-800 border-orange-300',
    'scheduler-retry': 'bg-purple-100 text-purple-800 border-purple-300',
};

const whatsappStatusConfig: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; className: string }> = {
    'connected': { label: 'Connected', icon: Wifi, className: 'bg-green-100 text-green-800 border-green-300' },
    'disconnected': { label: 'Disconnected', icon: WifiOff, className: 'bg-red-100 text-red-800 border-red-300' },
    'qrcode': { label: 'QR Code', icon: QrCode, className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    'unknown': { label: 'Unknown', icon: AlertCircle, className: 'bg-slate-100 text-slate-800 border-slate-300' },
};

const retryTriggerLabels: Record<string, string> = {
    'manual': 'Manual',
    'mqtt': 'MQTT',
    'fcm': 'FCM',
};

const retryTriggerColors: Record<string, string> = {
    'manual': 'bg-blue-100 text-blue-800 border-blue-300',
    'mqtt': 'bg-purple-100 text-purple-800 border-purple-300',
    'fcm': 'bg-indigo-100 text-indigo-800 border-indigo-300',
};

export function MessageLogMetadata({ metadata, className, retryCount }: MessageLogMetadataProps) {
    if (!metadata || (!metadata.attemptType && !metadata.whatsappStatus && !metadata.retryTrigger && !metadata.waVersion)) {
        return null;
    }

    // Calculate attempt label with retry visual
    const getAttemptLabel = () => {
        if (!metadata.attemptType) return '';
        
        const baseLabel = attemptTypeLabels[metadata.attemptType] || metadata.attemptType;
        
        // For retries, add attempt number based on retryCount
        // retryCount represents how many retries have been attempted so far
        // retryCount = 1 means this is retry #1, retryCount = 2 means retry #2, etc.
        if ((metadata.attemptType === 'client-retry' || metadata.attemptType === 'scheduler-retry') && retryCount !== undefined && retryCount > 0) {
            return `${baseLabel} #${retryCount}`;
        }
        
        return baseLabel;
    };

    return (
        <div className={cn("flex flex-wrap gap-2", className)}>
            {metadata.attemptType && (
                <Badge 
                    variant="outline" 
                    className={cn(attemptTypeColors[metadata.attemptType] || 'bg-slate-100 text-slate-800 border-slate-300')}
                >
                    {getAttemptLabel()}
                </Badge>
            )}
            {metadata.whatsappStatus && (() => {
                const config = whatsappStatusConfig[metadata.whatsappStatus.toLowerCase()] || whatsappStatusConfig['unknown'];
                const Icon = config.icon;
                return (
                    <Badge variant="outline" className={config.className}>
                        <Icon className="h-3 w-3" />
                        <span>WhatsApp: {config.label}</span>
                    </Badge>
                );
            })()}
            {metadata.retryTrigger && (
                <Badge 
                    variant="outline" 
                    className={cn(retryTriggerColors[metadata.retryTrigger] || 'bg-slate-100 text-slate-800 border-slate-300')}
                >
                    Trigger: {retryTriggerLabels[metadata.retryTrigger] || metadata.retryTrigger}
                </Badge>
            )}
            {metadata.waVersion && (
                <Badge 
                    variant="outline" 
                    className="bg-slate-50 text-slate-600 border-slate-200"
                >
                    <Info className="h-3 w-3 mr-1" />
                    WA: {metadata.waVersion}
                </Badge>
            )}
        </div>
    );
}

